import React, { useState } from 'react';
import styled from 'styled-components';
import { updateUserInTimeBasedCollection } from '../utils/firebaseUtils';
import { soundManager } from '../utils/soundUtils';
import exitPopupImg from '../assets/images/quiz-skip-img.png';
import prevBtnImg from '../assets/images/02register-prev-btn.png';
import okBtnImg from '../assets/images/02register-ok-btn.png';
import exitCoinGetImg from '../assets/images/skip-coin-get.png';
import hintOkBtnImg from '../assets/images/06quiz-hintOk-btn.png';

// EXIT 팝업 관련 스타일 컴포넌트들
const ExitPopup = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 49000;
  opacity: ${({ visible }) => visible ? 1 : 0};
  visibility: ${({ visible }) => visible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ExitPopupContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const ExitPopupImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ExitButtonContainer = styled.div`
  position: absolute;
  bottom: 30%;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 4dvw;
  z-index: 10;
`;

const ExitPrevButton = styled.img`
  width: 40dvw;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ExitOkButton = styled.img`
  width: 40dvw;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ExitCoinGetPopup = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 55000;
  opacity: ${({ visible }) => visible ? 1 : 0};
  visibility: ${({ visible }) => visible ? 'visible' : 'hidden'};
  transition: all 0.5s ease;
`;

const ExitCoinGetPopupContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const ExitCoinGetPopupImage = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ visible }) => visible ? 1 : 0};
  transform: ${({ visible }) => visible ? 'scale(1)' : 'scale(0.8)'};
  transition: all 0.5s ease;
`;

const ExitCoinGetOkButton = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: boolean }>`
  position: absolute;
  bottom: 17%;
  left: 50%;
  transform: translateX(-50%);
  width: 55%;
  cursor: pointer;
  opacity: ${({ visible }) => visible ? 1 : 0};
  transition: opacity 0.5s ease 0.3s;
  
  &:hover {
    transform: translateX(-50%) scale(1.05);
  }
  
  &:active {
    transform: translateX(-50%) scale(0.95);
  }
`;

interface ExitPopupProps {
  visible: boolean;
  onClose: () => void;
  onExit: () => void;
  exitPopupImage?: string;
  coinGetImage?: string;
  onNavigate?: (path: string) => void;
  navigatePath?: string;
  pageInfo?: string; // 페이지 정보 (예: 'quiz1', 'quiz2' 등)
  buttonType?: 'exit' | 'skip'; // 버튼 타입
}

const ExitPopupComponent: React.FC<ExitPopupProps> = ({ 
  visible, 
  onClose, 
  onExit, 
  exitPopupImage = exitPopupImg,
  coinGetImage = exitCoinGetImg,
  onNavigate,
  navigatePath = '/progress-card?from=exit',
  pageInfo = 'unknown',
  buttonType = 'exit'
}) => {
  const [showExitCoinGetPopup, setShowExitCoinGetPopup] = useState(false);

  // 사용자 데이터 업데이트 함수
  const updateUserDataForExit = async (): Promise<boolean> => {
    try {
      const currentUser = localStorage.getItem('currentUser');
      if (!currentUser) {
        console.error('로그인된 사용자 정보가 없습니다.');
        alert('로그인 정보를 찾을 수 없습니다. 다시 로그인해주세요.');
        return false;
      }

      const userData = JSON.parse(currentUser);
      
      // 모든 zone을 실패로 설정하고 코인을 200으로 설정
      const updateData: any = {
        coinCount: 200,
        'zoneClearStatus.zone1': '실패',
        'zoneClearStatus.zone2': '실패',
        'zoneClearStatus.zone3': '실패',
        'zoneClearStatus.zone4': '실패',
        'zoneClearStatus.zone5': '실패',
        'zoneClearStatus.zone6': '실패'
      };


      // 새로운 시간 기반 컬렉션 구조를 사용하여 사용자 데이터 업데이트
      await updateUserInTimeBasedCollection(
        userData.nickname, 
        userData.avatarNumber, 
        updateData
      );
      
      
      // 로컬 스토리지의 사용자 정보도 업데이트
      const updatedUserData = {
        ...userData,
        coinCount: updateData.coinCount,
        zoneClearStatus: {
          zone1: '실패',
          zone2: '실패',
          zone3: '실패',
          zone4: '실패',
          zone5: '실패',
          zone6: '실패'
        }
      };
      localStorage.setItem('currentUser', JSON.stringify(updatedUserData));
      
      return true;
    } catch (error) {
      console.error('EXIT 사용자 데이터 업데이트 중 오류:', error);
      alert('데이터 저장 중 오류가 발생했습니다. 다시 시도해주세요.');
      return false;
    }
  };

  const handleExitPopupClick = () => {
    // 팝업 배경 클릭 시 아무것도 하지 않음
  };

  const handleExitPrevClick = () => {
    // 이전 버튼 클릭 시 EXIT 팝업 닫기
    soundManager.playButtonClick();
    onClose();
  };

  const handleExitOkClick = () => {
    // 네 버튼 클릭 시 EXIT 팝업 닫고 코인 획득 팝업 표시
    soundManager.playButtonClick();
    setShowExitCoinGetPopup(true);
  };

  // 버튼 클릭 정보를 DB에 저장하는 함수
  const saveButtonClickInfo = async (page: string, buttonType: string, nickname: string) => {
    try {
      console.log('버튼 클릭 정보 저장 시작:', { page, buttonType, nickname });
      
      // 사용자 생성 후 2시간 유효하므로 현재 시간과 1시간 전 컬렉션만 확인
      const { db } = await import('../firebase');
      const { doc, updateDoc, getDoc, collection, query, where, getDocs } = await import('firebase/firestore');
      
      const { getTimeBasedCollectionName } = await import('../utils/firebaseUtils');
      
      console.log('🔍 사용자 검색 시작:', { nickname, page, buttonType });
      
      // 1. 현재 시간 컬렉션 검색
      const currentCollection = getTimeBasedCollectionName(new Date());
      console.log('📅 현재 컬렉션 검색:', currentCollection);
      
      let userFound = false;
      let targetCollection = '';
      let targetUserDoc = null;
      
      const currentRef = collection(db, currentCollection);
      const currentQuery = query(
        currentRef,
        where('nickname', '==', nickname)
      );
      
      const currentSnapshot = await getDocs(currentQuery);
      console.log('현재 컬렉션 검색 결과:', currentSnapshot.docs.length);
      
      if (!currentSnapshot.empty) {
        userFound = true;
        targetCollection = currentCollection;
        targetUserDoc = currentSnapshot.docs[0];
        console.log('✅ 현재 컬렉션에서 사용자 발견');
      } else {
        // 2. 1시간 전 컬렉션 검색 (사용자 생성 후 2시간 유효)
        const oneHourAgo = new Date();
        oneHourAgo.setHours(oneHourAgo.getHours() - 1);
        const oneHourAgoCollection = getTimeBasedCollectionName(oneHourAgo);
        
        console.log('📅 1시간 전 컬렉션 검색:', oneHourAgoCollection);
        
        const oneHourAgoRef = collection(db, oneHourAgoCollection);
        const oneHourAgoQuery = query(
          oneHourAgoRef,
          where('nickname', '==', nickname)
        );
        
        const oneHourAgoSnapshot = await getDocs(oneHourAgoQuery);
        console.log('1시간 전 컬렉션 검색 결과:', oneHourAgoSnapshot.docs.length);
        
        if (!oneHourAgoSnapshot.empty) {
          userFound = true;
          targetCollection = oneHourAgoCollection;
          targetUserDoc = oneHourAgoSnapshot.docs[0];
          console.log('✅ 1시간 전 컬렉션에서 사용자 발견');
        }
      }
      
      if (userFound && targetUserDoc) {
        const userRef = doc(db, targetCollection, targetUserDoc.id);
        
        console.log('🎯 사용자 문서 정보:', {
          id: targetUserDoc.id,
          collection: targetCollection,
          nickname: targetUserDoc.data().nickname
        });
        
        const userData = targetUserDoc.data();
        const buttonClicks = userData.buttonClicks || [];
        
        // 새로운 클릭 정보 추가
        const newClickInfo = {
          page: page,
          buttonType: buttonType,
          timestamp: new Date().toISOString(),
          date: new Date().toLocaleDateString('ko-KR')
        };
        
        buttonClicks.push(newClickInfo);
        console.log('➕ 추가할 클릭 정보:', newClickInfo);
        console.log('📊 업데이트될 전체 클릭 정보:', buttonClicks);
        
        // DB 업데이트
        await updateDoc(userRef, {
          buttonClicks: buttonClicks
        });
        
        console.log('✅ DB 업데이트 완료:', `${page}에서 ${buttonType} 버튼 클릭 정보가 저장되었습니다.`);
      } else {
        console.error('❌ 사용자를 찾을 수 없습니다:', nickname);
        console.error('검색한 컬렉션들:', {
          current: currentCollection,
          oneHourAgo: getTimeBasedCollectionName(new Date(Date.now() - 60 * 60 * 1000))
        });
      }
    } catch (error) {
      console.error('❌ 버튼 클릭 정보 저장 중 오류:', error);
    }
  };

  const handleExitCoinGetOkClick = async () => {
    // 코인 획득 팝업 확인 버튼 클릭 시 사용자 데이터 업데이트 후 EXIT 실행
    soundManager.playButtonClick();
    setShowExitCoinGetPopup(false);
    
    // 버튼 클릭 정보 저장
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      const userData = JSON.parse(currentUser);
      await saveButtonClickInfo(pageInfo, buttonType, userData.nickname);
    }
    
    const updateSuccess = await updateUserDataForExit();
    if (updateSuccess) {
      // onNavigate가 있으면 사용 (퀴즈 페이지에서), 없으면 새로고침 (ProgressCardPage에서)
      if (onNavigate) {
        onNavigate(navigatePath);
      } else {
        // 현재 URL의 파라미터를 그대로 유지하면서 새로고침
        const currentUrl = window.location.href;
        window.location.href = currentUrl;
      }
    }
  };

  return (
    <>
      <ExitPopup visible={visible} onClick={handleExitPopupClick}>
        <ExitPopupContent onClick={(e) => e.stopPropagation()}>
          <ExitPopupImage
            src={exitPopupImage}
            alt="EXIT 팝업"
          />
          <ExitButtonContainer>
            <ExitPrevButton
              src={prevBtnImg}
              alt="이전 버튼"
              onClick={handleExitPrevClick}
            />
            <ExitOkButton
              src={okBtnImg}
              alt="네 버튼"
              onClick={handleExitOkClick}
            />
          </ExitButtonContainer>
        </ExitPopupContent>
      </ExitPopup>
      <ExitCoinGetPopup visible={showExitCoinGetPopup}>
        <ExitCoinGetPopupContent onClick={(e) => e.stopPropagation()}>
          <ExitCoinGetPopupImage
            src={coinGetImage}
            alt="EXIT 코인 획득"
            visible={showExitCoinGetPopup}
          />
          <ExitCoinGetOkButton
            src={hintOkBtnImg}
            alt="확인 버튼"
            visible={showExitCoinGetPopup}
            onClick={handleExitCoinGetOkClick}
          />
        </ExitCoinGetPopupContent>
      </ExitCoinGetPopup>
    </>
  );
};

export default ExitPopupComponent;
